import React, { useCallback, useMemo } from 'react';
import * as S from './styles';
enum Region {
  'neighborhood' = 'neighborhood',
  'city' = 'city',
  'state' = 'state',
  'country' = 'country',
  'multi_city_vicinity' = 'multi_city_vicinity',
  'point_of_interest' = 'point_of_interest',
  'airport' = 'airport',
  'high_level_region' = 'high_level_region',
  'province_state' = 'province_state',
  'continent' = 'continent',
  'train_station' = 'train_station',
  'metro_station' = 'metro_station',
  'hotel' = 'hotel',
}
export type ContentSearchOptionProps = {
  label: string | string[];
  id: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  name?: string;
  type?: string;
  notDisplayLocationInSearch?: boolean;
  isFeatured?: boolean;
  icon?: JSX.Element;
  query?: string;
};
const generateAddress = ({
  name,
  city,
  state,
  country
}: {
  name?: string;
  city?: string;
  state?: string;
  country?: string;
}): string => {
  const nameExists = name != undefined;
  const cityExists = city != undefined;
  const stateExists = state != undefined;
  const countryExists = country != undefined;
  const address = `${nameExists ? `${name}` : ''}${cityExists ? `${nameExists ? ', ' : ''}${city}` : ''}${stateExists ? `${cityExists || nameExists ? ', ' : ''}${state}` : ''}${countryExists ? `${stateExists || cityExists || nameExists ? ', ' : ''}${country}` : ''}`;
  return address?.replace(/\(Arredores\)| \(Arredores\)|\(Cidade\)| \(Cidade\)|\(Ponto de Interesse\)| \(Ponto de Interesse\)|\(Ponto de interesse\)| \(Ponto de interesse\)|\(estado\)| \(estado\)|\(Estado\)| \(Estado\)|\(Estação de trem\)| \(Estação de trem\)|\(Bairro\)| \(Bairro\)|\(Estação de metrô\)| \(Estação de metrô\)/g, '');
};
const normalizeText = (text: string) => {
  return text.normalize('NFD') // Separa os acentos das letras
  .replace(/[\u0300-\u036f]/g, ''); // Remove os caracteres de acento
};
const queryRegex = (query: string): RegExp | null => {
  const normalizedQuery = normalizeText(query);
  try {
    return new RegExp(`(${normalizedQuery})`, 'gi');
  } catch {
    return null;
  }
};
const highlightText = (text?: string, query?: string): (JSX.Element | string)[] | string => {
  if (!query || !text) return text;
  const regex = queryRegex(query);
  if (!regex) {
    return text;
  }
  const normalizedText = normalizeText(text);
  const matches = Array.from(normalizedText.matchAll(regex));
  const highlightedElements: (JSX.Element | string)[] = [];
  let lastIndex = 0;
  for (const match of matches) {
    if (match.index === undefined || match.index === null) continue;
    const start = match.index;
    const end = start + match[0].length;

    // Adiciona a parte antes do highlight
    highlightedElements.push(text.slice(lastIndex, start));

    // Adiciona a parte destacada em <strong>
    highlightedElements.push(<strong key={start}>{text.slice(start, end)}</strong>);
    lastIndex = end;
  }

  // Adiciona a parte restante da string
  highlightedElements.push(text.slice(lastIndex));
  return highlightedElements;
};
const ContentSearchOption = (contentSearchOption: ContentSearchOptionProps): JSX.Element => {
  const {
    name,
    label,
    city,
    country,
    state,
    type,
    notDisplayLocationInSearch,
    query,
    isFeatured
  } = contentSearchOption || {};
  let nameOrLabel = name || label;
  nameOrLabel = typeof nameOrLabel == 'string' ? nameOrLabel : nameOrLabel?.join(', ');
  const handleAddress = useCallback((type: string) => {
    switch (type) {
      case Region.neighborhood:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.city:
        return generateAddress({
          city,
          state,
          country
        });
      case Region.state:
        return generateAddress({
          name: nameOrLabel,
          country
        });
      case Region.country:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.multi_city_vicinity:
        return generateAddress({
          city,
          state,
          country
        });
      case Region.point_of_interest:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.airport:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.high_level_region:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.province_state:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.continent:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.train_station:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.metro_station:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      case Region.hotel:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
      default:
        return generateAddress({
          name: nameOrLabel,
          city,
          state,
          country
        });
    }
  }, [city, country, nameOrLabel, state]);
  const renderOption = useMemo(() => {
    const parsedLabel = isFeatured ? nameOrLabel : handleAddress(type);
    const labelToDisplay = notDisplayLocationInSearch ? nameOrLabel : parsedLabel;
    return <S.Container>
        {contentSearchOption?.icon && <div className={`container-icon `}>{contentSearchOption.icon}</div>}
        <p className={`${isFeatured ? 'isFeatured' : ''} `}>{highlightText(labelToDisplay, query)}</p>
      </S.Container>;
  }, [contentSearchOption?.icon, handleAddress, isFeatured, nameOrLabel, notDisplayLocationInSearch, query, type]);
  return renderOption;
};
export default ContentSearchOption;